import { useEffect, useMemo, useState } from "react";
import { api, fmtBRL, fmtTime, STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Kanban as KanbanIcon, RefreshCw, GripVertical, User, MapPin, DollarSign, Clock, Layers, Users as UsersIcon } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { hasPerm } from "@/lib/roles";
import { WaitingTimer, pickWaitSince } from "@/components/WaitingTimer";

// Kanban visual das entregas — dois modos de visualização (fev/2026):
// - Por Status: 5 colunas (Aguardando/Em Trânsito/Concluída/Remarcar/Cancelada). Drag muda status.
// - Por Entregador: 1 coluna por entregador + Sem entregador. Drag REATRIBUI.
// Regra: em modo Status, cancelada/remarcar exige motivo. Em modo Entregador, drag → PUT entregador_id.

const STATUS_COLUMNS = [
  { id: "aguardando",             label: "Aguardando",              color: "bg-blue-50 border-blue-200",       header: "bg-blue-100 text-blue-900",       dot: "bg-blue-500" },
  { id: "aguardando_conferencia", label: "Aguardando Conferência",  color: "bg-violet-50 border-violet-200",   header: "bg-violet-100 text-violet-900",   dot: "bg-violet-500" },
  { id: "em_transito",            label: "Em Trânsito",             color: "bg-orange-50 border-orange-200",   header: "bg-orange-100 text-orange-900",   dot: "bg-orange-500" },
  { id: "concluida",              label: "Concluída",               color: "bg-emerald-50 border-emerald-200", header: "bg-emerald-100 text-emerald-900", dot: "bg-emerald-500" },
  { id: "remarcar",               label: "Remarcar",                color: "bg-amber-50 border-amber-200",     header: "bg-amber-100 text-amber-900",     dot: "bg-amber-500" },
  { id: "cancelada",              label: "Cancelada",               color: "bg-rose-50 border-rose-200",       header: "bg-rose-100 text-rose-900",       dot: "bg-rose-500" },
];

// Paleta de cores para colunas de entregadores (varia para diferenciar visualmente)
const ENTREGADOR_PALETTE = [
  { color: "bg-purple-50 border-purple-200",   header: "bg-purple-100 text-purple-900",   dot: "bg-purple-500" },
  { color: "bg-teal-50 border-teal-200",       header: "bg-teal-100 text-teal-900",       dot: "bg-teal-500" },
  { color: "bg-fuchsia-50 border-fuchsia-200", header: "bg-fuchsia-100 text-fuchsia-900", dot: "bg-fuchsia-500" },
  { color: "bg-cyan-50 border-cyan-200",       header: "bg-cyan-100 text-cyan-900",       dot: "bg-cyan-500" },
  { color: "bg-lime-50 border-lime-200",       header: "bg-lime-100 text-lime-900",       dot: "bg-lime-500" },
  { color: "bg-indigo-50 border-indigo-200",   header: "bg-indigo-100 text-indigo-900",   dot: "bg-indigo-500" },
];

const STATUS_BADGE = {
  aguardando:             "bg-blue-100 text-blue-800 border-blue-300",
  aguardando_conferencia: "bg-violet-100 text-violet-800 border-violet-300",
  em_transito:            "bg-orange-100 text-orange-800 border-orange-300",
  concluida:              "bg-emerald-100 text-emerald-800 border-emerald-300",
  remarcar:               "bg-amber-100 text-amber-800 border-amber-300",
  cancelada:              "bg-rose-100 text-rose-800 border-rose-300",
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function KanbanCard({ it, lookup, showStatus = false, isOverlay = false, canEdit = true }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: it.id,
    data: { entrega: it },
    disabled: !canEdit || isOverlay,  // sem perm entregas.a → drag desligado no dnd-kit
  });
  const style = isOverlay ? { boxShadow: "0 20px 40px rgba(0,0,0,0.25)" } : undefined;
  const bairro = lookup.bai[it.bairro_id] || "—";
  const entregador = lookup.ent[it.entregador_id] || "Sem entregador";
  const priority = it.prioridade;
  const cursorCls = canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default";
  // Substitui ternária aninhada por lookup (code review fev/2026)
  const priorityRing = { urgente: "ring-2 ring-red-300", vip: "ring-2 ring-purple-300" }[priority] || "";
  return (
    <div
      ref={setNodeRef}
      {...(canEdit ? attributes : {})}
      {...(canEdit ? listeners : {})}
      style={{ ...style, opacity: isDragging && !isOverlay ? 0.35 : 1 }}
      data-testid={`kanban-card-${it.id}`}
      title={canEdit ? undefined : "Somente leitura — sem permissão para alterar entregas"}
      className={`group bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition ${cursorCls} ${priorityRing}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        {canEdit && <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0 group-hover:text-slate-500" />}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Clock className="w-3 h-3" />{fmtTime(it.hr_saida) || "—"} · #{it.numero_pedido || it.numero_pedido_receita || "s/n"}
          </div>
          <div className="text-sm font-bold truncate">{it.cliente_nome || "Sem cliente"}</div>
        </div>
        {priority === "urgente" && <Badge className="bg-red-600 text-white text-[9px] shrink-0">🚨</Badge>}
        {priority === "vip" && <Badge className="bg-purple-600 text-white text-[9px] shrink-0">⭐</Badge>}
      </div>
      <div className="ml-5 space-y-0.5 text-xs text-slate-700">
        <div className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0 text-slate-400" />{bairro}</div>
        {!showStatus && (
          <div className="flex items-center gap-1 truncate"><User className="w-3 h-3 shrink-0 text-slate-400" />{entregador}</div>
        )}
        {showStatus && (
          <div className="flex items-center gap-1">
            <Badge className={`text-[9px] py-0 h-4 ${STATUS_BADGE[it.status] || "bg-slate-100"}`}>{STATUS[it.status] || it.status}</Badge>
          </div>
        )}
        {it.status === "aguardando_conferencia" && (
          <div className="pt-0.5">
            <WaitingTimer
              sinceIso={pickWaitSince(it)}
              size="sm"
              testId={`kanban-wait-timer-${it.id}`}
            />
          </div>
        )}
        {it.valor > 0 && (
          <div className="flex items-center gap-1 font-semibold text-orange-700"><DollarSign className="w-3 h-3 shrink-0" />{fmtBRL(it.valor)}</div>
        )}
      </div>
    </div>
  );
}

function Column({ col, cards, lookup, showStatus = false, subLabel, canEdit = true }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id, disabled: !canEdit });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${col.id}`}
      className={`flex flex-col rounded-xl border-2 ${col.color} ${isOver && canEdit ? "ring-4 ring-offset-2 ring-slate-400" : ""} transition-all min-h-[400px]`}
    >
      <div className={`px-3 py-2 rounded-t-lg font-semibold text-sm flex items-center justify-between ${col.header}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${col.dot}`} />
          <span className="truncate">{col.label}</span>
        </div>
        <Badge variant="outline" className="bg-white/70 text-xs shrink-0" data-testid={`kanban-count-${col.id}`}>{cards.length}</Badge>
      </div>
      {subLabel && (
        <div className="px-3 py-1 border-b border-white/70 text-[10px] text-slate-600 bg-white/40" data-testid={`kanban-sublabel-${col.id}`}>
          {subLabel}
        </div>
      )}
      <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-260px)]">
        {cards.length === 0 && (
          <div className="text-xs text-center text-muted-foreground py-8" data-testid={`kanban-empty-${col.id}`}>
            {canEdit ? "Solte cards aqui" : "Vazio"}
          </div>
        )}
        {cards.map((it) => <KanbanCard key={it.id} it={it} lookup={lookup} showStatus={showStatus} canEdit={canEdit} />)}
      </div>
    </div>
  );
}

export default function Kanban() {
  const { user, currentEmpresaId } = useAuth();
  // canEdit: requer `kanban.a` (alterar no Kanban) — permite ao admin negar drag-and-drop
  // sem retirar acesso completo às Entregas. Backward-compat: se usuário não tem `kanban`
  // na matrix (perms_matrix antiga), cai em `entregas.a` que sempre representou "pode alterar".
  const hasKanbanMatrix = !!(user?.perms_matrix && user.perms_matrix.kanban);
  const canEdit = hasKanbanMatrix
    ? hasPerm(user, "kanban", "a")
    : hasPerm(user, "entregas", "a");
  const [items, setItems] = useState([]);
  const [lookup, setLookup] = useState({ bai: {}, ent: {} });
  const [entregadoresList, setEntregadoresList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [motivoPrompt, setMotivoPrompt] = useState(null);
  const [date, setDate] = useState(todayStr());
  // Persistente para o supervisor não precisar re-selecionar toda vez
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("kanban_view_mode") || "status"; } catch { return "status"; }
  });
  useEffect(() => { try { localStorage.setItem("kanban_view_mode", viewMode); } catch { /* noop */ } }, [viewMode]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    setLoading(true);
    try {
      const [ent, bai, entg] = await Promise.all([
        api.get(`/entregas?start=${date}&end=${date}`),
        api.get("/bairros"),
        api.get("/entregadores"),
      ]);
      setItems(ent.data || []);
      setEntregadoresList(entg.data || []);
      setLookup({
        bai: Object.fromEntries((bai.data || []).map((x) => [x.id, x.nome])),
        ent: Object.fromEntries((entg.data || []).map((x) => [x.id, x.nome])),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar entregas");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // `load` fecha sobre `date` + APIs estáveis; reload correto vem de deps abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, currentEmpresaId]);

  const columns = useMemo(() => {
    if (viewMode === "entregador") {
      const cols = [
        { id: "__none__", label: "Sem entregador", color: "bg-slate-50 border-slate-300", header: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
      ];
      entregadoresList.forEach((e, i) => {
        const p = ENTREGADOR_PALETTE[i % ENTREGADOR_PALETTE.length];
        cols.push({ id: e.id, label: e.nome, ...p });
      });
      return cols;
    }
    return STATUS_COLUMNS;
  }, [viewMode, entregadoresList]);

  const grouped = useMemo(() => {
    const acc = Object.fromEntries(columns.map((c) => [c.id, []]));
    for (const it of items) {
      const key = viewMode === "entregador"
        ? (it.entregador_id && acc[it.entregador_id] ? it.entregador_id : "__none__")
        : (STATUS_COLUMNS.find((c) => c.id === it.status) ? it.status : "aguardando");
      if (acc[key]) acc[key].push(it);
    }
    return acc;
  }, [items, columns, viewMode]);

  // Sub-label por coluna: no modo entregador mostra breakdown mini (conc/pend)
  const subLabel = (colId) => {
    if (viewMode !== "entregador") return null;
    const list = grouped[colId] || [];
    const conc = list.filter((x) => x.status === "concluida").length;
    const pend = list.filter((x) => ["aguardando", "em_transito", "remarcar"].includes(x.status)).length;
    const canc = list.filter((x) => x.status === "cancelada").length;
    if (list.length === 0) return null;
    return `✅ ${conc} · 🕐 ${pend}${canc ? ` · ❌ ${canc}` : ""}`;
  };

  const applyStatusChange = async (it, newStatus, motivo) => {
    const prev = it.status;
    setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, status: newStatus, motivo: motivo || x.motivo } : x));
    try {
      const payload = { status: newStatus };
      if (motivo) payload.motivo = motivo;
      await api.put(`/entregas/${it.id}`, payload);
      toast.success(`${it.cliente_nome || "Entrega"} → ${STATUS[newStatus] || newStatus}`);
      load();
    } catch (e) {
      setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, status: prev } : x));
      toast.error(e?.response?.data?.detail || "Falha ao mover entrega");
    }
  };

  const applyEntregadorChange = async (it, newEid) => {
    const prev = it.entregador_id;
    setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, entregador_id: newEid } : x));
    try {
      await api.put(`/entregas/${it.id}`, { entregador_id: newEid });
      const nomeNovo = newEid ? (lookup.ent[newEid] || "novo entregador") : "Sem entregador";
      toast.success(`${it.cliente_nome || "Entrega"} → ${nomeNovo}`);
      load();
    } catch (e) {
      setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, entregador_id: prev } : x));
      toast.error(e?.response?.data?.detail || "Falha ao reatribuir entregador");
    }
  };

  const onDragStart = (evt) => {
    const it = items.find((x) => x.id === evt.active.id);
    setActiveCard(it || null);
  };
  const onDragEnd = (evt) => {
    setActiveCard(null);
    if (!canEdit) { toast.error("Sem permissão para alterar entregas"); return; }
    const overId = evt.over?.id;
    if (!overId) return;
    const it = items.find((x) => x.id === evt.active.id);
    if (!it) return;
    if (viewMode === "entregador") {
      const newEid = overId === "__none__" ? null : overId;
      if (it.entregador_id === newEid) return;
      applyEntregadorChange(it, newEid);
      return;
    }
    // viewMode === status
    if (it.status === overId) return;
    if (overId === "cancelada" || overId === "remarcar") {
      setMotivoPrompt({ it, targetStatus: overId, motivo: "" });
      return;
    }
    applyStatusChange(it, overId);
  };

  const submitMotivo = async () => {
    if (!motivoPrompt) return;
    const m = (motivoPrompt.motivo || "").trim();
    if (!m) { toast.error("Motivo é obrigatório"); return; }
    const { it, targetStatus } = motivoPrompt;
    setMotivoPrompt(null);
    await applyStatusChange(it, targetStatus, m);
  };

  const gridCols = viewMode === "entregador"
    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
    : "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3";

  return (
    <div className="space-y-4" data-testid="page-kanban">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="p-3 rounded-xl bg-orange-600 text-white"><KanbanIcon className="w-6 h-6" /></div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-3xl font-extrabold leading-tight">Kanban de Entregas</h1>
          <p className="text-sm text-muted-foreground">
            {/* Extrai subtítulo pra remover ternária aninhada (code review fev/2026) */}
            {(() => {
              if (!canEdit) return "Modo somente-leitura — você não tem permissão para alterar entregas.";
              if (viewMode === "status") return "Arraste os cards entre as colunas para mudar o status.";
              return "Arraste os cards entre as colunas para reatribuir o entregador.";
            })()}
          </p>
        </div>
        {/* Toggle de modo de visualização */}
        <div className="flex rounded-md border border-slate-200 overflow-hidden shadow-sm" role="tablist" data-testid="kanban-view-toggle">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "status"}
            onClick={() => setViewMode("status")}
            className={`px-3 h-9 text-xs font-semibold inline-flex items-center gap-1.5 transition ${viewMode === "status" ? "bg-orange-600 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
            data-testid="kanban-view-status"
          >
            <Layers className="w-3.5 h-3.5" />Por Status
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "entregador"}
            onClick={() => setViewMode("entregador")}
            className={`px-3 h-9 text-xs font-semibold inline-flex items-center gap-1.5 transition ${viewMode === "entregador" ? "bg-orange-600 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
            data-testid="kanban-view-entregador"
          >
            <UsersIcon className="w-3.5 h-3.5" />Por Entregador
          </button>
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="kanban-date" />
        <Button onClick={load} disabled={loading} variant="outline" data-testid="btn-kanban-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Atualizar
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className={gridCols}>
          {columns.map((c) => (
            <Column
              key={c.id}
              col={c}
              cards={grouped[c.id] || []}
              lookup={lookup}
              showStatus={viewMode === "entregador"}
              subLabel={subLabel(c.id)}
              canEdit={canEdit}
            />
          ))}
        </div>
        <DragOverlay>
          {activeCard ? <KanbanCard it={activeCard} lookup={lookup} showStatus={viewMode === "entregador"} isOverlay canEdit /> : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={!!motivoPrompt} onOpenChange={(o) => { if (!o) setMotivoPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="kanban-motivo-dialog">
          <DialogHeader>
            <DialogTitle>
              Motivo para {motivoPrompt?.targetStatus === "cancelada" ? "cancelar" : "remarcar"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {motivoPrompt?.it?.cliente_nome || "Entrega"} — informe por que a entrega será movida.
            </p>
            <Textarea
              placeholder="Ex: Cliente ausente, pediu para retornar às 15h..."
              value={motivoPrompt?.motivo || ""}
              onChange={(e) => setMotivoPrompt((p) => p ? { ...p, motivo: e.target.value } : null)}
              data-testid="kanban-motivo-input"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setMotivoPrompt(null)}>Cancelar</Button>
              <Button onClick={submitMotivo} className="bg-orange-600 hover:bg-orange-700" data-testid="kanban-motivo-submit">
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
